import { isEmpty } from 'lodash';
import { UseFormSetError } from '@fiverr-private/forms';
import { useIdentificationContext } from '@fiverr-private/identification_core';
import { signUp } from '../../../../services/signUp';
import { createSocialUser } from '../../../../services/createSocialUser';
import { globalBigQueryReporter, globalGrafanaReporter, globalMixpanelReporter } from '../../../../events';
import { useStepsContext } from '../../../StepsContext';
import { translate } from '../../../../shared/utils/translate';
import { raiseGeneralErrorToast } from '../../../../shared/utils/raiseGeneralErrorToast';
import { setShowQuestionnaireCookie } from '../../../../shared/utils/setShowQuestionnaireCookie';
import { ProfileSetupSchema } from '../../ProfileSetupStep.constants';
import { logError } from '../../../../shared/utils/logError';

interface Props {
    setLoading: (loading: boolean) => void;
    setError: UseFormSetError<ProfileSetupSchema>;
    isSocialFlow?: boolean;
}

export const useSubmitHandler = ({ setLoading, setError, isSocialFlow = false }: Props) => {
    const {
        password,
        email,
        setRedirectUrl,
        skipQuestionnaire,
        skipActivation,
        finishSigningFlowAndNavigate,
        enableShouldNavigateOnClose,
    } = useIdentificationContext();
    const { goToStep } = useStepsContext();

    const handleSignUp = (username: string) => {
        if (isSocialFlow && !email) {
            logError({
                message: 'Profile Setup Step: email is missing on social submit',
            });
            return;
        }

        if (isSocialFlow) {
            return createSocialUser({
                email,
                username,
                funnel: 'standard',
            });
        }

        if (!email || !password) {
            logError({
                message: 'Profile Setup Step: email or password is missing on default submit',
            });
            return;
        }

        return signUp({
            email,
            password,
            username,
            funnel: 'standard',
        });
    };

    const onSubmit = async (data: ProfileSetupSchema) => {
        try {
            setLoading(true);

            globalGrafanaReporter.count('identification.%{platform}.profile_setup.submit');
            globalMixpanelReporter.send('Identification - Profile Setup - Submit');
            globalBigQueryReporter.send('identification.profile_setup.submit');

            const result = await handleSignUp(data.username);

            if (!result) {
                return;
            }

            const { redirectUrl, errorKeys } = result;

            if (!isEmpty(errorKeys)) {
                const error = errorKeys.username?.length ? errorKeys.username[0] : 'unspecified';

                setError('username', {
                    message: translate(`validation.username.${error}`),
                });
                return;
            }

            if (!skipQuestionnaire) {
                setShowQuestionnaireCookie();
            }

            if (redirectUrl) {
                setRedirectUrl(redirectUrl);
            }

            globalGrafanaReporter.count('identification.%{platform}.signup.completed');
            // Should be removed. Duplicating in "finishSigningFlowAndNavigate"
            globalMixpanelReporter.send('Identification - Completed');
            globalBigQueryReporter.send('identification.completed');

            if (skipActivation) {
                finishSigningFlowAndNavigate();
                return;
            }

            enableShouldNavigateOnClose();

            goToStep('code_activation', {
                resetHistory: true,
            });
        } catch (error) {
            globalMixpanelReporter.send('Identification - Failed', {
                errorType: error instanceof Error ? error.message : 'unspecified',
            });
            globalBigQueryReporter.send('identification.failed');
            globalGrafanaReporter.count('identification.%{platform}.signup.failed');

            raiseGeneralErrorToast();
        } finally {
            setLoading(false);
        }
    };

    return onSubmit;
};
